import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = '';

export default function WhatsAppButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    work_email: '',
    requirement: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_BASE_URL}/api/whatsapp-leads`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! You can now connect with us on WhatsApp.');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi, I'm ${formData.name}. I'm interested in learning more about international property investment. My requirement: ${formData.requirement}`
    );
    window.open(`https://wa.me/918999570132?text=${message}`, '_blank');
    setIsOpen(false);
    setIsSubmitted(false);
    setFormData({ name: '', work_email: '', requirement: '' });
  };

  return (
    <>
      {/* WhatsApp Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg shadow-[#25D366]/30 flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </motion.button>

      {/* WhatsApp Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl overflow-hidden"
              data-testid="whatsapp-modal"
            >
              {/* Header */}
              <div className="bg-[#25D366] p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Chat with Us</h3>
                    <p className="text-white/80 text-xs">Typically replies within minutes</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {!isSubmitted ? (
                  <>
                    <p className="text-[#A1A1AA] text-sm mb-6">
                      Please share your details so our advisor can assist you better on WhatsApp.
                    </p>
                    
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="whatsapp-form">
                      <div>
                        <Label className="text-white mb-2 block">Full Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter your full name"
                          className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#25D366] focus:ring-[#25D366]"
                          required
                          data-testid="whatsapp-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Work Email *</Label>
                        <Input
                          type="email"
                          value={formData.work_email}
                          onChange={(e) => setFormData({...formData, work_email: e.target.value})}
                          placeholder="your@company.com"
                          className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#25D366] focus:ring-[#25D366]"
                          required
                          data-testid="whatsapp-email-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Your Requirement *</Label>
                        <Textarea
                          value={formData.requirement}
                          onChange={(e) => setFormData({...formData, requirement: e.target.value})}
                          placeholder="Tell us about your investment interests, preferred countries, budget range..."
                          className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#25D366] focus:ring-[#25D366] min-h-[100px]"
                          required
                          data-testid="whatsapp-requirement-input"
                        />
                      </div>

                      <Button 
                        type="submit"
                        className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
                        disabled={isSubmitting}
                        data-testid="whatsapp-submit-btn"
                      >
                        {isSubmitting ? 'Submitting...' : 'Continue to WhatsApp'}
                        <Send className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 rounded-full bg-[#25D366]/20 flex items-center justify-center mx-auto mb-4">
                      <Check className="h-8 w-8 text-[#25D366]" />
                    </div>
                    <h4 className="text-white font-medium mb-2">Details Submitted!</h4>
                    <p className="text-[#A1A1AA] text-sm mb-6">
                      Click below to start chatting with our investment advisor on WhatsApp.
                    </p>
                    <Button 
                      onClick={openWhatsApp}
                      className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
                      data-testid="whatsapp-open-btn"
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2 fill-current">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Open WhatsApp
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
